// src/utils/bookingProvenance.js
//
// Pass 82D §10/§11 — HUMAN-FRIENDLY, TRUTHFUL provenance for the live UI. Provenance is DERIVED from canonical state
// (externalSource / source / sourceType / assignment-evidence fields), never a separately-maintained duplicate label
// (§11 — one truth). A record may combine authority from more than one source (commercial booking from the SAS master
// schedule + instructor allocation from the SAS grid) — we surface BOTH, never flattened into one misleading badge.
//
// PURE (no Firestore, no React).

import {
  SOURCE_IDS, isSourceBackedCommercial, isManualNative, isOperationalHold, MANUAL_SOURCE_IDS,
} from './sourceAuthority';
import { PRODUCT_NAME } from '../config/product';

// id → human label (§10). Never expose raw programmer ids in primary UI. A native (operator-created, not source-backed)
// record is labelled with the product brand (config-driven); the `motus_native`/`motus` tokens below remain stable
// internal enum/style keys, not visible brand.
const SOURCE_LABELS = {
  [SOURCE_IDS.SAS_MASTER]: 'SAS Booking Schedule',
  [SOURCE_IDS.SAS_MASTER_STAFF]: 'SAS Staff Schedule',
  [SOURCE_IDS.SAS_GRID]: 'SAS Schedule Overview',
  [SOURCE_IDS.SAS_COLLECTIVE]: 'Collective Schedule',
  manual: PRODUCT_NAME, manual_sas: PRODUCT_NAME, app: PRODUCT_NAME, operator: PRODUCT_NAME, ui: PRODUCT_NAME, admin: PRODUCT_NAME, org_direct: PRODUCT_NAME, admin_reassign: PRODUCT_NAME,
};

export function humanSourceLabel(sourceId = '') {
  const s = String(sourceId || '').trim();
  return SOURCE_LABELS[s] || (s ? 'External source' : '');
}

// Which source assigned the instructor (allocation authority). Manual/operator assignment → Motus; a grid/collective
// evidence source → that source's label; unassigned → null. Derived from the canonical assignment fields only.
function allocationSource(booking = {}) {
  if (!booking.instructorId) return null;
  const evid = String(booking.assignmentEvidenceSource || '').trim();
  if (evid === SOURCE_IDS.SAS_GRID || evid === SOURCE_IDS.SAS_COLLECTIVE) return evid;
  const asrc = String(booking.assignmentSource || '').trim();
  if (asrc === SOURCE_IDS.SAS_GRID) return SOURCE_IDS.SAS_GRID;
  if (asrc === 'collective_pull_in') return SOURCE_IDS.SAS_COLLECTIVE;
  // org_direct / admin_reassign / manual / '' → a Motus operator assignment
  return SOURCE_IDS.MANUAL;
}

// Truthful, non-flattened provenance for a booking.
export function bookingProvenance(booking = {}) {
  const sourceBacked = isSourceBackedCommercial(booking);
  const manualNative = isManualNative(booking) && !sourceBacked;
  const hold = isOperationalHold(booking);

  // COMMERCIAL authority (who owns the booking record itself).
  let commercial = null;
  if (sourceBacked) commercial = { sourceId: SOURCE_IDS.SAS_MASTER, label: humanSourceLabel(SOURCE_IDS.SAS_MASTER), kind: 'commercial_booking' };
  else if (hold) commercial = { sourceId: booking.externalSource || SOURCE_IDS.SAS_GRID, label: humanSourceLabel(booking.externalSource || SOURCE_IDS.SAS_GRID), kind: 'operational_allocation' };
  else if (manualNative) commercial = { sourceId: SOURCE_IDS.MANUAL, label: PRODUCT_NAME, kind: 'motus_native' };

  // ALLOCATION authority (who assigned the instructor) — kept SEPARATE from the commercial authority (§10).
  const allocId = allocationSource(booking);
  const allocation = allocId ? { sourceId: allocId, label: humanSourceLabel(allocId), kind: allocId === SOURCE_IDS.MANUAL ? 'motus_native' : 'assignment_evidence' } : null;

  const badges = [];
  if (commercial) badges.push({ label: commercial.label, tone: commercial.kind === 'motus_native' ? 'motus' : 'source', title: manualNative ? `${PRODUCT_NAME}-native record` : 'Managed by the source schedule' });
  // only show a distinct allocation badge when it differs from the commercial source (don't repeat "SAS…/SAS…")
  if (allocation && (!commercial || allocation.sourceId !== commercial.sourceId) && allocation.label !== (commercial && commercial.label)) {
    badges.push({ label: `Allocation · ${allocation.label}`, tone: allocation.kind === 'motus_native' ? 'motus' : 'source', title: 'Instructor allocation authority' });
  }

  return {
    commercial,
    allocation,
    isSourceBacked: sourceBacked,
    isManualNative: manualNative,
    isOperationalHold: hold,
    badges,
    // convenience for a compact single-line label
    primaryLabel: commercial ? commercial.label : (manualNative ? PRODUCT_NAME : ''),
  };
}

// A short, human "who owns this" line for tooltips/detail (§10). Truthful about combined authority.
export function provenanceSummary(booking = {}) {
  const p = bookingProvenance(booking);
  if (p.isManualNative) return `${PRODUCT_NAME}-native — created in ${PRODUCT_NAME}, not on the source schedule.`;
  if (p.commercial && p.allocation && p.allocation.sourceId !== p.commercial.sourceId) {
    return `Booking: ${p.commercial.label} · Allocation: ${p.allocation.label}`;
  }
  if (p.commercial) return `Managed by ${p.commercial.label}`;
  return '';
}

export { MANUAL_SOURCE_IDS };
